// Libraries
import { Updater } from '@tanstack/react-table';
import { NextPage } from 'next';
import { AppProps } from 'next/app';
import { ReactElement, ReactNode } from 'react';
// Utils
import { Rating } from '@constants/index';

/**
 * Type definition for Next.js page component with layout support.
 * @typeParam P - The type of page props.
 * @typeParam IP - The type of initial page props.
 * @extends {NextPage<P, IP>}
 */
export type NextPageWithLayout<P = object, IP = P> = NextPage<P, IP> & {
  /**
   * A function that returns the layout component for the page.
   * @param {React.ReactElement} page - The page component to be wrapped in the layout.
   * @returns {React.ReactNode} The wrapped page component with layout.
   */
  getLayout?: (page: ReactElement) => ReactNode;
};

/**
 * Props for the App component with a layout.
 * @extends {AppProps}
 */
export type AppPropsWithLayout = AppProps & {
  /**
   * The component to be rendered.
   */
  Component: NextPageWithLayout;
};

/**
 * Represents the status of the NextAuth authentication state.
 */
export enum Status {
  /**
   * The user is authenticated.
   */
  authenticated = 'authenticated',
  /**
   * The authentication state is loading.
   */
  loading = 'loading',
  /**
   * The user is unauthenticated.
   */
  unauthenticated = 'unauthenticated',
}

/**
 * Represents the breakpoints used in the Chakra UI library.
 */
export enum Breakpoints {
  /**
   * Extra large breakpoint. Equivalent to 96em (1536px).
   */
  '2xl' = '2xl',
  /**
   * Extra large breakpoint. Equivalent to 80em (1280px).
   */
  xl = 'xl',
  /**
   * Large breakpoint. Equivalent to 62em (992px).
   */
  lg = 'lg',
  /**
   * Medium breakpoint. Equivalent to 48em (768px).
   */
  md = 'md',
  /**
   * Small breakpoint. Equivalent to 30em (480px).
   */
  sm = 'sm',
  /**
   * Base breakpoint. Equivalent to 0em (0px).
   */
  base = 'base',
}

/**
 * Represents the most common sizes.
 */
export enum Sizes {
  /**
   * Large size.
   */
  lg = 'lg',
  /**
   * Medium size.
   */
  md = 'md',
  /**
   * Small size.
   */
  sm = 'sm',
}

/**
 * Represents the keys of the Rating enum.
 */
export type RatingKeys = keyof typeof Rating;

/**
 * Represents the available options for DateRangeType.
 */
export enum DateRangeType {
  /**
   * Represents the "end of year" date range type.
   */
  endOfYear = 'eoy',

  /**
   * Represents the "year to date" date range type.
   */
  yearToDate = 'ytd',
}

/**
 * Represents the available options for DateRangeRatingType.
 */
export enum DateRangeRatingType {
  /**
   * Represents the "end of year" rating date range type.
   */
  eoyRating = 'eoyRating',

  /**
   * Represents the "year to date" rating date range type.
   */
  ytdRating = 'ytdRating',
}

/**
 * Represents the keys of the Breakpoints enum.
 */
export type BreakpointsKeys = keyof typeof Breakpoints;

export type FixedSizeArray<N extends number, T> = N extends 0
  ? never[]
  : {
      0: T;
      length: N;
    } & ReadonlyArray<T>;

/**
 * Enum representing utility components.
 */
export enum UtilityComponents {
  /**
   * Text component.
   */
  Text = 'Text',
  /**
   * Compacted rating information component.
   */
  RatingInfoCompacted = 'RatingInfoCompacted',
  /**
   * IconText component.
   */
  IconText = 'IconText',
}

/**
 * Represents a unique identifier for a client.
 */
export type ClientId = number;

/**
 * Represents the International Maritime Organization (IMO) number of a vessel.
 */
export type Imo = number;

/**
 * Represents the parameters required for vessel data, including the client ID and IMO number.
 */
export type VesselControllerParams = {
  /**
   * The unique identifier for the client.
   */
  clientId: ClientId;

  /**
   * Names optional to filter the vessels by name.
   */
  names?: string[];

  /**
   * The International Maritime Organization (IMO) number of the vessel.
   */
  imo: Imo;
};

/**
 * Represents the parameters required for voyage data, including the client ID, IMO number and voyage ID.
 */
export type VesselVoyageControllerParams = VesselControllerParams & {
  /**
   * The unique identifier for the voyage.
   */
  voyageId: string;
};

// TODO: Remove this type once we have the Rating scores already created
/**
 * Represents a rating category.
 */
export type RatingCategory = 'A' | 'B' | 'C' | 'D' | 'E' | 'N';

/**
 * Represents a factor in the calculation of FOC (Fuel Oil Consumption).
 */
export type FocFactor = 'sea_temperature' | 'current' | 'wind' | 'wave' | 'fouling' | 'speed';

/**
 * Enum representing different timeframe for vessel performance data.
 *
 * @enum {string}
 */
export enum TimeframeKey {
  /**
   * Timeframe for year-to-date vessel performance data.
   */
  YearToDate = 'yearToDate',

  /**
   * Timeframe for vessel performance data from the past 12 months.
   */
  Past12Months = 'past12Months',

  /**
   * Timeframe for vessel performance data since the last underwater cleaning.
   */
  SinceLastUnderwaterCleaning = 'sinceLastUnderwaterCleaning',

  /**
   * Timeframe for vessel performance data since the last drydock.
   */
  SinceLastDrydock = 'sinceLastDrydock',

  /**
   * Timeframe for all-time vessel performance data.
   */
  AllTime = 'allTime',
}

/**
 * Enum representing different factors affecting speed consumption.
 *
 * @enum {string}
 */
export enum SpeedConsumptionFactor {
  /**
   * Speed consumption data for ballast condition.
   */
  Ballast = 'ballast',

  /**
   * Speed consumption data for ballast condition after drydock.
   */
  BallastAfterDrydock = 'ballastAfterDrydock',

  /**
   * Speed consumption data for laden condition.
   */
  Laden = 'laden',

  /**
   * Speed consumption data for laden condition after drydock.
   */
  LadenAfterDrydock = 'ladenAfterDrydock',
}

type Resistance = {
  timestamp: string;
  resistance: number;
};

/**
 * Represents a trend of added resistance data points.
 */
export type AddedResistanceTrend = Resistance[];

/**
 * Represents a trend of added resistance data points.
 */
export type UnderwaterCleaningEvents = Resistance[];

/**
 * Represents a trend of added resistance data points.
 */
export type DryDockEvents = Resistance[];

/**
 * Represents a data point for speed consumption curve.
 */
export type SpeedConsumptionPoint = {
  /**
   * The fuel oil consumption (FOC) value at the given speed.
   */
  foc: number;

  /**
   * The speed value at which the FOC is measured.
   */
  speed: number;
};

/**
 * Represents a speed consumption curve for a specific factor.
 */
export type SpeedConsumptionCurve = {
  /**
   * The factor affecting speed consumption.
   */
  factor: SpeedConsumptionFactor;

  /**
   * The array of data points representing the speed consumption curve.
   */
  values: SpeedConsumptionPoint[];
};

/**
 * Represents rating ranges for gradient styling in the chart.
 * The keys of this object correspond to the rating keys used in the chart data.
 * Each value is an array representing the minimum and maximum values for the range of that rating key.
 * The format is [minValue, maxValue].
 */
export type RatingRanges = Record<RatingKeys, [number, number]>;

export type SetColumnFilters<ColumnFiltersState> = (updater: Updater<ColumnFiltersState>) => void;

/**
 * Enum representing different line styles for chart legends.
 *
 * @enum {string}
 */
export enum ChartLineStyles {
  /**
   * Solid black line style.
   */
  black = 'black',
  /**
   * Dashed black line style.
   */
  blackDashed = 'blackDashed',
  /**
   * Solid light-colored line style.
   */
  light = 'light',
  /**
   * Dashed light-colored line style.
   */
  lightDashed = 'lightDashed',
  /**
   * Dotted black line style.
   */
  blackDotted = 'blackDotted',

  /**
   * Rating lines style.
   */
  A = 'A',
  B = 'B',
  C = 'C',
  D = 'D',
  E = 'E',

  /**
   * Dashed rating line style.
   */
  ADashed = 'ADashed',
  BDashed = 'BDashed',
  CDashed = 'CDashed',
  DDashed = 'DDashed',
  EDashed = 'EDashed',

  /**
   * Gaps rating line style.
   */
  AtoB = 'AtoB',
  AtoC = 'AtoC',
  AtoD = 'AtoD',
  AtoE = 'AtoE',
  BtoA = 'BtoA',
  BtoC = 'BtoC',
  BtoD = 'BtoD',
  BtoE = 'BtoE',
  CtoA = 'CtoA',
  CtoB = 'CtoB',
  CtoD = 'CtoD',
  CtoE = 'CtoE',
  DtoA = 'DtoA',
  DtoB = 'DtoB',
  DtoC = 'DtoC',
  DtoE = 'DtoE',
  EtoA = 'EtoA',
  EtoB = 'EtoB',
  EtoC = 'EtoC',
  EtoD = 'EtoD',

  /**
   * Dashed gaps rating line style.
   */
  AtoBDashed = 'AtoBDashed',
  AtoCDashed = 'AtoCDashed',
  AtoDDashed = 'AtoDDashed',
  AtoEDashed = 'AtoEDashed',
  BtoADashed = 'BtoADashed',
  BtoCDashed = 'BtoCDashed',
  BtoDDashed = 'BtoDDashed',
  BtoEDashed = 'BtoEDashed',
  CtoADashed = 'CtoADashed',
  CtoBDashed = 'CtoBDashed',
  CtoDDashed = 'CtoDDashed',
  CtoEDashed = 'CtoEDashed',
  DtoADashed = 'DtoADashed',
  DtoBDashed = 'DtoBDashed',
  DtoCDashed = 'DtoCDashed',
  DtoEDashed = 'DtoEDashed',
  EtoADashed = 'EtoADashed',
  EtoBDashed = 'EtoBDashed',
  EtoCDashed = 'EtoCDashed',
  EtoDDashed = 'EtoDDashed',
}

export enum BroadcastAction {
  Logout = 'Logout',
}

/**
 * Structure of the message to be broadcasted in login/logout scenarios.
 *
 * This messages inform all tabs/windows loaded with the application authentication actions.
 */
export type LogoutBroadcastMessage = {
  /**
   * Auth action, e.g. login, logout
   */
  action: BroadcastAction;
  /**
   * Additional details to be passed in the message.
   */
  detail: string;
};
